"use client";

import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { syncPreflight, sync } from "@/lib/api/generated";
import type { SyncPreflightResponse } from "@/lib/api/model";
import { showToast } from "@/components/ui/sonner";
import { ApiError } from "@/lib/api/error";
import type { PipelineActionBody } from "@/features/jobs/store/pipeline/selection";
import { BULK_SELECTION_COPY } from "@/features/jobs/components/view/pipeline/bulk-selection-limit";
import { invalidateAfterSync } from "../../lib/invalidate-after-sync";
import {
  clearSyncProgressSession,
  loadSyncProgressSession,
  saveSyncProgressSession,
} from "../../lib/sync-progress-session-storage";
import { useJobAtsLink } from "../../hooks/use-job-ats-link";
import { SyncConfirmModal } from "./sync-confirm-modal";
import {
  SyncProgressToast,
  type SyncProgressSession,
} from "./sync-progress-toast";

export type SyncFlowOpenInput =
  | {
      jobId: string;
      jobCandidateIds: string[];
      /** Optional display names keyed by job_candidate_id, for row labels. */
      names?: Record<string, string>;
      onDone?: () => void;
    }
  | {
      jobId: string;
      selection: PipelineActionBody;
      onDone?: () => void;
    };

export interface SyncSelections {
  enrichByCandidate: Record<string, boolean>;
  emailByCandidate: Record<string, string | null>;
}

interface Ctx {
  isOpen: boolean;
  jobId: string | null;
  jobCandidateIds: string[];
  names: Record<string, string>;
  preflight: SyncPreflightResponse | undefined;
  preflightLoading: boolean;
  preflightError: boolean;
  submitting: boolean;
  insufficient: boolean;
  open(input: SyncFlowOpenInput): void;
  close(): void;
  submit(selections: SyncSelections): Promise<void>;
  /** Re-run sync preflight after filling application answers (#20). */
  refetchPreflight(): Promise<void>;
}

const SyncFlowCtx = React.createContext<Ctx | null>(null);

export function SyncFlowProvider({
  children,
  /** When set, restore an in-flight SyncJob toast after refresh for this job. */
  resumeJobId,
}: {
  children: React.ReactNode;
  resumeJobId?: string;
}) {
  const queryClient = useQueryClient();
  const [state, setState] = React.useState<SyncFlowOpenInput | null>(null);
  const [insufficient, setInsufficient] = React.useState(false);
  const [progressSession, setProgressSession] =
    React.useState<SyncProgressSession | null>(() =>
      resumeJobId ? loadSyncProgressSession(resumeJobId) : null,
    );
  const { syncDirection } = useJobAtsLink(
    state?.jobId ?? resumeJobId ?? null,
  );

  React.useEffect(() => {
    if (!resumeJobId) return;
    const saved = loadSyncProgressSession(resumeJobId);
    if (saved) setProgressSession(saved);
  }, [resumeJobId]);

  const selectionKey = !state
    ? ""
    : "jobCandidateIds" in state
      ? `ids:${[...state.jobCandidateIds].sort().join(",")}`
      : `bulk:${JSON.stringify(state.selection)}`;
  const preflightQuery = useQuery({
    queryKey: ["sync-preflight", state?.jobId, selectionKey],
    enabled: !!state,
    queryFn: () => {
      const current = state!;
      const body =
        "jobCandidateIds" in current
          ? { job_candidate_ids: current.jobCandidateIds }
          : current.selection;
      return syncPreflight(current.jobId, body);
    },
  });

  React.useEffect(() => {
    if (preflightQuery.data?.truncated) {
      showToast.warning(BULK_SELECTION_COPY.selectAllTruncated);
    }
  }, [preflightQuery.data]);

  const close = React.useCallback(() => {
    setState(null);
    setInsufficient(false);
  }, []);

  const open = React.useCallback((input: SyncFlowOpenInput) => {
    setInsufficient(false);
    setState(input);
  }, []);

  const handleProgressFinished = React.useCallback((sessionId: string) => {
    setProgressSession((s) => {
      if (s?.sessionId !== sessionId) return s;
      clearSyncProgressSession(s.jobId);
      return null;
    });
  }, []);

  const submitMut = useMutation({
    mutationFn: async ({
      enrichByCandidate,
      emailByCandidate,
    }: SyncSelections) => {
      const current = state;
      if (!current) throw new Error("Sync flow closed before submitting");
      const pf = preflightQuery.data!;
      const items = (pf.syncable ?? []).map((c) => ({
        job_candidate_id: c.job_candidate_id,
        enrich: !!enrichByCandidate[c.job_candidate_id],
        email: emailByCandidate[c.job_candidate_id] ?? null,
      }));
      const res = await sync(current.jobId, { items });
      // Snapshot everything onSuccess needs *before* awaits resolve, so a
      // dismiss (Esc/backdrop) that nulls `state` mid-flight can't make the
      // callback dereference a null `state`.
      return {
        res,
        jobId: current.jobId,
        onDone: current.onDone,
        ats: pf.ats_display_name ?? "your ATS",
        direction: syncDirection,
        blockedCount: pf.hard_blocked?.length ?? 0,
      };
    },
    onSuccess: ({ res, jobId, onDone, ats, direction, blockedCount }) => {
      invalidateAfterSync(queryClient, jobId, { pipeline: false });
      if (!res.sync_job_id) {
        showToast.error("Failed to sync profiles");
        return;
      }
      const session: SyncProgressSession = {
        sessionId: crypto.randomUUID(),
        jobId,
        syncJobId: res.sync_job_id,
        ats,
        direction,
        blockedCount,
        toastId: `sync-progress:${crypto.randomUUID()}`,
        startedAt: Date.now(),
      };
      saveSyncProgressSession(session);
      setProgressSession(session);
      onDone?.();
      setInsufficient(false);
      setState(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        setInsufficient(true);
        return;
      }
      showToast.error("Failed to sync profiles");
    },
  });

  const refetchPreflight = React.useCallback(async () => {
    await preflightQuery.refetch();
  }, [preflightQuery]);

  const value: Ctx = {
    isOpen: !!state,
    jobId: state?.jobId ?? null,
    jobCandidateIds:
      state && "jobCandidateIds" in state ? state.jobCandidateIds : [],
    names: state && "names" in state ? (state.names ?? {}) : {},
    preflight: preflightQuery.data,
    preflightLoading: preflightQuery.isLoading,
    preflightError: preflightQuery.isError,
    submitting: submitMut.isPending,
    insufficient,
    open,
    close,
    submit: async (selections) => {
      if (submitMut.isPending) return;
      await submitMut.mutateAsync(selections);
    },
    refetchPreflight,
  };

  return (
    <SyncFlowCtx.Provider value={value}>
      {children}
      <SyncConfirmModal />
      {progressSession && (
        <SyncProgressToast
          key={progressSession.sessionId}
          session={progressSession}
          onFinished={handleProgressFinished}
        />
      )}
    </SyncFlowCtx.Provider>
  );
}

export function useSyncFlowContext(): Ctx {
  const ctx = React.useContext(SyncFlowCtx);
  if (!ctx) throw new Error("useSyncFlow must be used within SyncFlowProvider");
  return ctx;
}

/** Public trigger surface for entry points. */
export function useSyncFlow() {
  const { open } = useSyncFlowContext();
  return { open };
}
